import MojitoCore from 'mojito/core';
import { isEmpty } from 'mojito/utils';
import { prefetchDataSuccess } from './actions.js';
import EventUtils from 'services/sports-content/events/utils.js';
import {
    STORE_KEY as PROMOTIONS_STORE_KEY,
    actions as promotionsActions,
} from 'services/promotions/slice.js';
import {
    STORE_KEY as SPORTS_STORE_NAME,
    actions as sportsActions,
} from 'services/sports-content/sports/slice.js';
import {
    STORE_KEY as EVENTS_STORE_NAME,
    actions as eventsActions,
} from 'services/sports-content/events/slice.js';
import {
    STORE_KEY as EVENT_GROUPS_STORE_NAME,
    actions as eventGroupsActions,
} from 'services/eventgroups/slice.js';
import {
    actions as sportMetaInfoActions,
    STORE_KEY as SPORT_META_INFORMATION_STORE_NAME,
} from 'services/sports-content/sport-meta-information/slice.js';

const reduxInstance = MojitoCore.Services.redux;
const transporterFactory = MojitoCore.Services.transporterFactory;

/**
 * A class for listening to prefetched data. Fills corresponding state slices depending on the data type.
 *
 * @class PrefetchListener
 * @name prefetchListener
 * @memberof Mojito.Services.Prefetch
 */
class PrefetchListener {
    /**
     * Initialize prefetch listener and start listening.
     *
     * @param {string} language - Language code to be used for the prefetch listener.
     * @function Mojito.Services.Prefetch.prefetchListener#init
     */
    init(language) {
        this.language = language;
        this.transporter = transporterFactory.getTransporter();
        this.startListening();
    }

    /**
     * Updates a Ngen repository collection with the provided data from prefetch.
     *
     * @param {string} collection - Collection name of ngen repository to update.
     * @param {Array} data - Item data to be added to the collection.
     * @param {boolean} [isLocaleInHeader = true] - Whether the locale is included in the header for this collection.
     * @function Mojito.Services.Prefetch.prefetchListener#updateNgenRepository
     */
    updateNgenRepository(collection, data, isLocaleInHeader = true) {
        this.transporter?.registerCollection(collection, this.language, isLocaleInHeader);
        data.forEach(item => {
            this.transporter?.addDataToCollection(collection, item, this.language, true);
        });
    }

    /**
     * Determines if the store content should be seeded with prefetched content.
     *
     * @param {*} storeContent - The current content in the store.
     * @param {*} prefetchContent - The prefetched content to potentially seed.
     * @returns {boolean} True if the store is empty and prefetched content is not empty.
     * @function Mojito.Services.Prefetch.prefetchListener#shouldSeedContent
     */
    shouldSeedContent(storeContent, prefetchContent) {
        return isEmpty(storeContent) && !isEmpty(prefetchContent);
    }

    /**
     * Attaches listener and start listening for `prefetchDataSuccess` action.
     * Action payload contains prefetched data.
     * When the action is dispatched, it updates the corresponding state slices and Ngen repository collections.
     *
     * @function Mojito.Services.Prefetch.prefetchListener#startListening
     */
    startListening() {
        reduxInstance.actionListener.startListening({
            actionCreator: prefetchDataSuccess,
            effect: ({ payload }, { getState, dispatch }) => {
                const {
                    container,
                    eventGroups,
                    markets,
                    events,
                    promotions,
                    sportsMetaInformation: sportMetaInformation,
                } = payload;
                const sports = container?.items;

                const state = getState();
                const eventsState = state[EVENTS_STORE_NAME];
                const sportsState = state[SPORTS_STORE_NAME];
                const promotionsState = state[PROMOTIONS_STORE_KEY];
                const eventGroupsState = state[EVENT_GROUPS_STORE_NAME];
                const sportMetaState = state[SPORT_META_INFORMATION_STORE_NAME];

                const shouldSeedSportMeta = this.shouldSeedContent(
                    sportMetaState.sportMetaInformation,
                    sportMetaInformation
                );
                const shouldSeedSports = this.shouldSeedContent(sportsState.sports, sports);
                const shouldSeedEvents = this.shouldSeedContent(eventsState.events, events);
                const shouldSeedMarkets = this.shouldSeedContent(eventsState.markets, markets);
                const shouldSeedPromotions = this.shouldSeedContent(
                    promotionsState.promotions,
                    promotions
                );
                const shouldSeedEventGroups = this.shouldSeedContent(
                    eventGroupsState.eventGroups,
                    eventGroups
                );

                if (shouldSeedSportMeta) {
                    this.updateNgenRepository('sportMetaInformation', sportMetaInformation);
                    const sportMetaPayload = sportMetaInformation.map(data => ({
                        id: data.id,
                        data,
                    }));
                    dispatch(sportMetaInfoActions.updateSportMetaInformationList(sportMetaPayload));
                }

                if (shouldSeedSports) {
                    dispatch(sportsActions.updateSports({ sports }));
                    this.updateNgenRepository('container', [container]);
                }

                if (shouldSeedEvents) {
                    const eventsPayload = events.map(data => ({ id: data.id, data }));
                    dispatch(eventsActions.updateEvents(eventsPayload));
                    this.updateNgenRepository('events', events);
                }

                if (shouldSeedMarkets) {
                    const marketsInMarketLines = EventUtils.getMarketsDataByMarketLine(
                        markets,
                        events
                    );
                    const marketsInPromotions = EventUtils.getMarketsDataByPromotions(
                        markets,
                        promotions
                    );
                    dispatch(
                        eventsActions.updateMarkets([
                            ...marketsInMarketLines,
                            ...marketsInPromotions,
                        ])
                    );
                    this.updateNgenRepository('markets', markets);
                }

                if (shouldSeedPromotions) {
                    dispatch(
                        promotionsActions.updatePromotions({
                            data: promotions,
                            channel: promotions.id,
                        })
                    );
                    this.updateNgenRepository('promotions', [promotions], false);
                }

                if (shouldSeedEventGroups) {
                    const eventGroupsPayload = eventGroups.map(data => ({ id: data.id, data }));
                    dispatch(eventGroupsActions.updateEventGroups(eventGroupsPayload));
                    this.updateNgenRepository('eventgroups', eventGroups);
                }
            },
        });
    }
}

export default new PrefetchListener();
